import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import ReturnsApi from "@/api/ReturnsApi";
import moment from "moment-timezone";
import {
  IMaintenanceReturnResult,
  IPartnerProductSearchResult,
  IResultEntity,
  IWarrantyInfo,
  ShipperType,
  IRootState,
  IReturnsState,
  IProductNameMap,
} from "@iloq/shared";
import { handleCommonApiErrors } from "@/api/CommonApiErrorHandler";

const returnsApi = new ReturnsApi();

const initialState: IReturnsState = {
  list: [],
  productNameCache: {},
  loading: false,
  error: false,
};

const actions: ActionTree<IRootState, IRootState> = {
  createProductReturn(
    { commit },
    payload: any,
  ): Promise<void | IResultEntity | IMaintenanceReturnResult> {
    return returnsApi
      .createProductReturn(payload.productReturn)
      .then((data) => {
        commit("addReturnResult", data);
        return data;
      })
      .catch((err) => {
        handleCommonApiErrors(err, false);
      });
  },
  listReturns({ commit }, email: string): Promise<void> {
    commit("setReturnLoading", true);
    return returnsApi
      .listProductReturns(email)
      .then((data: any) => {
        const res: [] = data;
        if (res && (res as any).indexOf("No product returns found") >= 0) {
          commit("setReturnResults", []);
        } else {
          res.sort((a: any, b: any) => (moment(a.created).isAfter(moment(b.created)) ? -1 : 1));
          commit("setReturnResults", res);
        }
      })
      .catch((e) => {
        commit("setReturnError", true);
        handleCommonApiErrors(e);
      })
      .finally(() => {
        commit("setReturnLoading", false);
      });
  },
  getShipperType(_: any, { countryCode, zipCode, city }): Promise<ShipperType> {
    return returnsApi.getShipperType(countryCode, zipCode, encodeURIComponent(city));
  },
  getWarrantyInfo(_: any, serialNumber: string): Promise<IWarrantyInfo> {
    return returnsApi.getWarrantyInfo(serialNumber);
  },
  async fetchProductNames({ state, commit, dispatch }, skus: string[]): Promise<void> {
    const searchResults: IPartnerProductSearchResult[] = await Promise.all(
      skus
        // fetch only by skus that are not in cache
        .filter((sku) => !state.productNameCache[sku])
        .map(async (sku) => {
          const preparedSku = sku.replaceAll(".", "_");
          return dispatch(
            "productSearch/searchArchivedProductsQuick",
            { term: preparedSku, top: 1 },
            { root: true },
          );
        }),
    );

    const productNameMap = searchResults
      .flatMap(({ value }) => value)
      .map((result) => ({
        [result.sku]: `${result.product_name}${result.subtitle ? ` - ${result.subtitle}` : ""}`,
      }))
      .reduce((prev, curr) => ({ ...prev, ...curr }), {});
    commit("addToProductNameCache", productNameMap);
  },
};

const mutations: MutationTree<IReturnsState> = {
  addReturnResult(state: IReturnsState, result: any): void {
    state.list.unshift(result);
  },
  setReturnResults(state: IReturnsState, result: any): void {
    state.list = result;
  },
  setReturnLoading(state: IReturnsState, loading: boolean): void {
    state.loading = loading;
  },
  setReturnError(state: IReturnsState, error: boolean): void {
    state.error = error;
  },
  addToProductNameCache(state: IReturnsState, productNames: IProductNameMap): void {
    state.productNameCache = {
      ...state.productNameCache,
      ...productNames,
    };
  },
};

export const returnsModule = {
  actions,
  mutations,
  state: initialState,
  namespaced: true,
};
